import React, { createContext, useCallback, useContext, useState } from "react";
import type { Dict, Lang } from "./types";
import { core } from "./core";
import { owner } from "./owner";
import { tenant } from "./tenant";
import { kn } from "./kn";
import { bn } from "./bn";
import { gu } from "./gu";
import { ml } from "./ml";
import { mr } from "./mr";
import { pa } from "./pa";

export type { Lang } from "./types";

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "bn", label: "বাংলা" },
  { code: "te", label: "తెలుగు" },
  { code: "mr", label: "मराठी" },
  { code: "ta", label: "தமிழ்" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

// en/hi/te/ta live split across core.ts/owner.ts/tenant.ts (grouped by
// screen area) and get merged here. Every language added since then is a
// single standalone flat Dict — see the comment on LangDict in types.ts.
const STANDALONE: Partial<Record<Lang, Dict>> = { kn, bn, gu, ml, mr, pa };

function mergeLang(lang: Lang): Dict {
  const fromCoreFiles = { ...core[lang], ...owner[lang], ...tenant[lang] };
  return Object.keys(fromCoreFiles).length ? fromCoreFiles : STANDALONE[lang] ?? {};
}

const translations: Record<Lang, Dict> = Object.fromEntries(
  LANGUAGES.map((l) => [l.code, mergeLang(l.code)])
) as Record<Lang, Dict>;

function lookup(dict: Dict, key: string): string | undefined {
  const dot = key.indexOf(".");
  if (dot === -1) return undefined;
  const ns = key.slice(0, dot);
  const field = key.slice(dot + 1);
  return dict[ns]?.[field];
}

interface LanguageState {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageState | null>(null);
const STORAGE_KEY = "toletbook_lang";
const VALID: Lang[] = LANGUAGES.map((l) => l.code);

function loadLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  return (VALID as string[]).includes(saved ?? "") ? (saved as Lang) : "en";
}

// True once the user has ever picked a language (even English) — used to
// show the one-time language-picker screen right after the boot splash on
// a fresh install, and never again afterward.
export function hasChosenLanguage(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(loadLang);

  const setLang = useCallback((l: Lang) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let raw = lookup(translations[lang], key) ?? lookup(translations.en, key) ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) raw = raw.split(`{${k}}`).join(String(v));
      }
      return raw;
    },
    [lang]
  );

  return React.createElement(LanguageContext.Provider, { value: { lang, setLang, t } }, children);
}

export function useLanguage(): LanguageState {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}
